import React, { Component } from "react"
import PropTypes from "prop-types"

import isEmpty from "lodash/isEmpty"

import ConnectButton from "./connect_button"

import ConnectedApplet from "./connected_applet"
import DisconnectedApplet from "./disconnected_applet"
import GrantAccess from "./grant_access"

import ConfigurationDescriptions from "./configuration_descriptions"
import Description from "./description"

import AppletFeedback from "app/components/platform_feedback/applet_feedback"
import { shouldUseAlternateBrandColor } from "shared/lib/alternate_brand_color"

import RubberBandPinchColor from "app/scripts/rubber_band_pinch_color"

import MobileAlert from "./mobile_alert"

import { connect } from "react-redux"
import { subscribe } from "redux-subscriber"

import {
  grantAccess,
  jumpToNextStep,
  requirementsMet,
} from "./reducers/connections"

import { loginUser } from "./reducers/user"

import debounce from "lodash/debounce"

import { disableApplet, updateApplet } from "./reducers/applet"

import {
  updateNextToBeConnected,
  connectClicked,
  clearLocalStorage,
  updateAnimationCollection,
  appletCreated,
  appletDisabled,
  appletEnabled,
  updateAppletConfigurations,
  logEvent,
} from "./actions"

import TopLevelConfig from "./top_level_config"
import ArchiveDelete from "./archive_delete"
import AppletRecommendations from "./applet_recommendations"

export const ConnectionDetails = ({
  applet,
  editPath,
  onConfigurationSwitchChange,
}) => (
  <>
    <ConfigurationDescriptions
      applet={applet}
      editPath={editPath}
      onConfigurationSwitchChange={onConfigurationSwitchChange}
    />
    <Description text={applet.description} />
  </>
)

export class Connection extends Component {
  knobBgColorForDarkBrands = false

  constructor(props) {
    super(props)

    this.state = {
      pushEnabled: props.applet.push_enabled,
    }

    // change the knob color when the applet has a dark color
    if (shouldUseAlternateBrandColor(props.applet.brand_color)) {
      this.knobBgColorForDarkBrands = true
    }

    this.calculateOnResize = debounce(() => {
      this.setState({
        connectButtonMaxFontSize: this.calculateConnectButtonFontSize(),
      })
    }, 100)
  }

  componentDidMount() {
    // update state in case this applet was saved elsewhere
    // the enable state always comes from the applet payload
    if (this.props.applet.status === "enabled_for_user") {
      this.props.setAsCreated(true)
      this.props.setAsEnabled(true)
      this.props.setAsDisabled(false)
    }

    const unsubAppletEnabled = subscribe("applet.enabled", state => {
      if (state.applet.enabled) {
        unsubAppletEnabled()
        this.props.clearSavedState()

        // communicate to everything on the page the applet has been enabled
        window.dispatchEvent(
          new CustomEvent("appletEnabled", { detail: { enabled: true } })
        )
      }
    })

    // take user to log in page if not logged in
    subscribe("connections.connectClicked", state => {
      !state.user.signedIn && this.props.requestLogin()
    })

    const unsubConnectClick = subscribe("connections.connectClicked", state => {
      // fire the event only when click to enable
      if (state.connections.connectClicked) {
        unsubConnectClick()

        const needsGrant = this.props.services.filter(p => {
          return !p.connected
        })

        if (isEmpty(needsGrant)) {
          this.props.logAction({
            eventName: "statechange",
            eventData: {
              object_type: "connection",
              object_id: this.props.applet.id,
              location_type: "connection",
              state: "services_checked",
            },
          })
        }
      }
    })

    // force that no static applets will be displayed with a connections path
    if (
      !this.props.programmable &&
      window.location.href.includes("connections")
    ) {
      window.history.pushState(
        {},
        "",
        `/applets/${this.props.applet.friendly_id}`
      )
    }

    // make the rubberband area the same color as the connection card header
    RubberBandPinchColor({ checkPath: true })

    window.addEventListener("resize", this.calculateOnResize)
  }

  componentWillUnmount() {
    window.removeEventListener("resize", this.calculateOnResize)
  }

  calculateConnectButtonFontSize = () => {
    if (document.body.clientWidth < 724) {
      // phone
      return 22
    } else if (document.body.clientWidth > 1444) {
      // large desktop
      return 30
    } else {
      // small desktops and tablets
      return 28
    }
  }

  state = {
    connectButtonMaxFontSize: this.calculateConnectButtonFontSize(),
  }

  executeConnectionFlow = () => {
    // don't jump to a step if the flow is finalized in the system of record
    const finalStates = ["disabled_for_user", "enabled_for_user"]
    !finalStates.includes(this.props.applet.status) &&
      this.props.connectClicked &&
      this.props.jumpToNextStep()
  }

  render() {
    const {
      connectClicked,
      disconnectClicked,
      showFeedbackForm,
      urls,
      applet,
      user,
      sessionId,
    } = this.props
    const { status } = applet

    const redirectToLimit = () => {
      window.location.href = "/limit"
    }

    const userOwnsApplet = applet.author === user.login
    const needsRedirect = !userOwnsApplet && this.props.userAtQuota

    return (
      <>
        {status === "enabled_for_user" ? (
          <>
            <ConnectedApplet
              mainService={this.props.mainService}
              connectClicked={connectClicked}
              programmable={this.props.programmable}
              onDisconnectClick={this.props.disableApplet}
              appletConnected={this.props.appletConnected}
              knobBgColorForDarkBrands={this.knobBgColorForDarkBrands}
              connectButtonMaxFontSize={this.state.connectButtonMaxFontSize}
            />

            <MobileAlert
              user={this.props.user}
              userDevice={this.props.userDevice}
              applet={this.props.applet}
              appStorePath={this.props.appStorePath}
              googlePlayStorePath={this.props.googlePlayStorePath}
            />
          </>
        ) : status === "disabled_for_user" ? (
          <DisconnectedApplet
            mainService={this.props.mainService}
            connectClicked={connectClicked}
            disconnectClicked={disconnectClicked}
            services={this.props.services}
            applet={this.props.applet}
            programmable={this.props.programmable}
            knobBgColorForDarkBrands={this.knobBgColorForDarkBrands}
            onConnectClick={e =>
              needsRedirect ? redirectToLimit() : this.props.reConnectApplet(e)
            }
            appletConnected={this.props.appletConnected}
            animation={this.props.animation}
            updateAnimationCol={this.props.updateAnimationCol}
            connectButtonMaxFontSize={this.state.connectButtonMaxFontSize}
          />
        ) : connectClicked ? (
          <GrantAccess
            programmable={this.props.programmable}
            mainService={this.props.nextServiceToBeConnected}
            services={this.props.services}
            onAccessGranted={this.props.onAccessGranted}
            appletConnected={this.props.appletConnected}
            lastServiceConnected={this.props.lastServiceConnected}
            animation={this.props.animation}
            updateAnimationCol={this.props.updateAnimationCol}
            requirementsMet={this.props.requirementsMet}
            appletIsSaving={this.props.appletIsSaving}
            executeConnectionFlow={this.executeConnectionFlow}
            connectButtonMaxFontSize={this.state.connectButtonMaxFontSize}
          />
        ) : (
          <ConnectButton
            mainService={this.props.mainService}
            connected={this.props.applet.status === "enabled_for_user"}
            onConnectClick={e =>
              needsRedirect ? redirectToLimit() : this.props.onConnectClick(e)
            }
            programmable={this.props.programmable}
            knobBgColorForDarkBrands={this.knobBgColorForDarkBrands}
            connectButtonMaxFontSize={this.state.connectButtonMaxFontSize}
            playEnableAnimation
          />
        )}

        <ConnectionDetails
          applet={this.props.applet}
          editPath={this.props.editPath}
          onConfigurationSwitchChange={this.props.updateAppletConfigurations}
        />
        {this.props.applet.status && (
          <TopLevelConfig applet={applet} user={user} urls={urls} />
        )}

        {showFeedbackForm ? (
          <AppletFeedback
            appletId={this.props.applet.id}
            serviceName={this.props.mainService.name}
            iftttNext={true}
          />
        ) : (
          <></>
        )}

        <ArchiveDelete
          applet={applet}
          user={user}
          myAppletURL={urls.myAppletURL}
          sessionId={sessionId}
        />

        <AppletRecommendations applet={applet} />

        <div className="breath-space" />
        <div className="connection-id">
          ID <span>{this.props.applet.id}</span>
        </div>
      </>
    )
  }
}

const mapStateToProps = state => {
  const { config, applet, user, connections, error, animation } = state
  const services = Object.values(connections.permissions)

  return {
    services,
    user,
    applet: applet.applet,
    mainService: connections.mainService,
    nextServiceToBeConnected:
      connections.permissions[connections.nextToBeConnected] ||
      connections.mainService,
    connectClicked: connections.connectClicked,
    disconnectClicked: connections.disconnectClicked,
    coalesced: user.coalesced,
    appletConnected: applet.enabled,
    returnTo: config.returnTo,
    appStorePath: config.appStorePath,
    googlePlayStorePath: config.googlePlayStorePath,
    automation: config.automation,
    needsConfig: config.needsConfig,
    editPath: config.editPath,
    userDevice: config.userDevice,
    error,
    lastServiceConnected: connections.lastServiceConnected,
    programmable: config.programmable,
    animation: animation.collection,
    appletIsSaving: applet.appletIsSaving,
    showFeedbackForm: config.showFeedbackForm,
    myAppletURL: config.myAppletURL,
  }
}

const mapDispatchToProps = dispatch => {
  return {
    // reflect the applet status, noop but for updating the local state
    setAsCreated: status => dispatch(appletCreated(status)),
    setAsDisabled: status => dispatch(appletDisabled(status)),
    setAsEnabled: status => dispatch(appletEnabled(status)),

    // call the server
    disableApplet: () => dispatch(disableApplet()),
    reConnectApplet: () => dispatch(updateApplet()),

    // remeber, everthing is async so this is your friend if you have to find
    // if all requirements are met and you're go to connect the applet
    requirementsMet: () => dispatch(requirementsMet()),

    nextToBeConnected: service => dispatch(updateNextToBeConnected(service)),
    onConnectClick: () => dispatch(connectClicked(true)),
    onAccessGranted: service => dispatch(grantAccess(service)),
    jumpToNextStep: () => dispatch(jumpToNextStep()),
    requestLogin: () => dispatch(loginUser()),
    logAction: event => dispatch(logEvent(event)),
    clearSavedState: () => dispatch(clearLocalStorage()),
    updateAnimationCol: state => dispatch(updateAnimationCollection(state)),
    updateAppletConfigurations: state =>
      dispatch(updateAppletConfigurations(state)),
  }
}

export default connect(mapStateToProps, mapDispatchToProps)(Connection)

Connection.propTypes = {
  ...TopLevelConfig.propTypes,
  ...MobileAlert.propTypes,
  applet: PropTypes.object.isRequired,
  services: PropTypes.array,
  mainService: PropTypes.object,
  nextServiceToBeConnected: PropTypes.object,
  programmable: PropTypes.bool,
  appletConnected: PropTypes.bool,
  jumpToNextStep: PropTypes.func,
  onConnectClick: PropTypes.func,
  connectClicked: PropTypes.bool,
  disconnectClicked: PropTypes.bool,
  onAccessGranted: PropTypes.func,
  nextToBeConnected: PropTypes.func,
  returnTo: PropTypes.string,
  requestLogin: PropTypes.func,
  logAction: PropTypes.func,
  updateAppletConfigurations: PropTypes.func,
  reConnectApplet: PropTypes.func,
  error: PropTypes.object,
  automation: PropTypes.bool,
  needsConfig: PropTypes.bool,
  editPath: PropTypes.string,
  lastServiceConnected: PropTypes.object,
  setAsDisabled: PropTypes.func,
  disableApplet: PropTypes.func,
  animation: PropTypes.object,
  updateAnimationCol: PropTypes.func,
  setAsCreated: PropTypes.func,
  setAsEnabled: PropTypes.func,
  requirementsMet: PropTypes.func,
  clearSavedState: PropTypes.func,
  appletIsSaving: PropTypes.bool,
  showFeedbackForm: PropTypes.bool,
}
